import { makeStyles } from '@mui/styles';
import { primary } from 'apollo-react/colors';
import Grid from 'apollo-react/components/Grid';
import ToggleButton from 'apollo-react/components/ToggleButton';
import StarOutline from 'apollo-react-icons/StarOutline';
import StarSolid from 'apollo-react-icons/StarSolid';
import _ from 'lodash';
import qs from 'query-string';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { getPillText } from './selectors';
import { toggleFavorite } from '../../actions/FavoritesActions';
import { downloadFullEntity } from '../../actions/OfflineDataActions';
import { DownloadButton } from '../../components/buttons/Download';
import HelpLink from '../../components/HelpLink';
import RecordRevision from '../../components/RecordRevision';
import { PILGRIM_TOP_MENU_HEIGHT } from '../../constants/SizeConstants';
import { useFormSelector } from '../../custom-hooks';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { useWindowDimensions } from '../../custom-hooks/useWindowDimensions';
import { FORM_KEYS } from '../../form-designer/constants/form-manager.constants';
import { DOWNLOAD_ICON_ACTIONS_TYPES } from '../../form-designer/constants/runtime.constants';
import DynamicField from '../../form-designer/DynamicField';
import {
  getFavorites,
  getNetworkStatus,
  getWorkflowInfo,
  makeGetEntityPropertyValue,
} from '../../form-designer/selectors/common.selectors';
import {
  getHeaderInfo,
  getIsDownloadInOfflineAvailableForEntity,
} from '../../form-designer/selectors/form-manager.selectors';
import { getDownloadTypeForSavedSearchRow } from '../../form-designer/selectors/runtime.selectors';
import { findFavorite, getHeaderTitleText } from '../../form-designer/utils/common.utils';
import { equalityFn } from '../../form-designer/utils/form-manager.utils';
import { getUITA } from '../../utils/commonUtils';
import { isEmpty } from '../../utils/ramdaUtils';

const useStyles = makeStyles({
  dashboardHeaderStyled: {
    minHeight: 48,
    fontFamily: 'Proxima Nova',
    fontSize: ({ isMobileDevice }) => (isMobileDevice ? 30 : 32),
    fontWeight: 600,
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: 1.7,
    letterSpacing: 'normal',
    color: '#000000',
    marginRight: ({ isMobileDevice }) => (isMobileDevice ? 0 : 15),
  },
  styledPaper: {
    backgroundColor: '#f8f9fb',
    display: 'inline-block',
  },
  pill: {
    height: 24,
    borderRadius: 16,
    backgroundColor: 'purple',
    textAlign: 'center',
    fontFamily: 'Proxima Nova',
    fontSize: 14,
    fontWeight: 600,
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: 1.9,
    letterSpacing: 'normal',
    color: '#ffffff',
    padding: '0 18px',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  firstRow: {
    display: 'flex',
    alignItems: 'center',
  },
  favoriteButton: {
    marginTop: '-7px',
    '& svg': {
      width: '1em',
      height: '1em',
    },
    '& button': {
      width: 40,
      height: 40,
    },
  },
  detailView: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    '& div': {
      display: 'flex',
      flex: 0,
      flexBasis: 'fit-content',
      width: 'fit-content',
      minWidth: 'fit-content',
      paddingRight: 12,
    },
  },
  firstRowMobile: {
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 12,
    position: 'sticky',
    top: PILGRIM_TOP_MENU_HEIGHT,
    backgroundColor: '#f8f9fb',
  },
  pillIconContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'stretch',
    alignItems: 'center',
    gap: 5,
  },
  downloadButton: {
    marginLeft: '5px',
  },
  starIcon: {
    fontSize: '1.7rem',
  },
});

const EntityDetailsHeader = () => {
  const dispatch = useDispatch();
  const translateString = useTranslation();
  const userKey = useFormSelector(makeGetEntityPropertyValue, { propertyName: 'UserKey' });
  const recordNumber = useFormSelector(makeGetEntityPropertyValue, {
    propertyName: 'RecordNumberColumn',
  });
  const revisionNumber = useFormSelector(makeGetEntityPropertyValue, {
    propertyName: 'RecordRevisionColumn',
  });
  const favorites = useSelector(getFavorites);
  const isOnline = useSelector(getNetworkStatus);
  const pillText = useSelector(getPillText(FORM_KEYS.STEP));
  const location = useLocation();
  const headerElement = useFormSelector(getHeaderInfo, { pathname: location.pathname }, equalityFn);
  const { isMobileDevice } = useWindowDimensions();
  const classes = useStyles({ isMobileDevice });

  const queryString = location.search;
  const qsParams = qs.parse(queryString) || {};
  const { stepId, workflowId } = useFormSelector(
    getWorkflowInfo,
    { formKey: FORM_KEYS.STEP },
    equalityFn
  );
  const entityId = `${qsParams.itmId ?? ''}_${stepId ?? qsParams.stepId ?? ''}`;
  const actionType = useSelector(getDownloadTypeForSavedSearchRow(entityId));
  const isDownloadInOfflineAvailableForEntity = useFormSelector(
    getIsDownloadInOfflineAvailableForEntity,
    { formKey: FORM_KEYS.STEP }
  );

  const headerTitle = (
    <>
      <span ui-ta="RecordNumber">{recordNumber ?? userKey}</span>
      {revisionNumber && <RecordRevision revision={revisionNumber} />}
    </>
  );

  const headerTitleText = getHeaderTitleText({ userKey, recordNumber, revisionNumber });

  const isFavorite = favorites.findIndex(findFavorite(qsParams.entityType, headerTitleText)) !== -1;
  const showDownloadButton =
    isDownloadInOfflineAvailableForEntity &&
    !(actionType === DOWNLOAD_ICON_ACTIONS_TYPES.DOWNLOAD && !isOnline);

  useEffect(() => {
    document.title = !isEmpty(headerTitleText) ? headerTitleText : 'SmartSolve';
  }, [headerTitleText]);

  const handleFavorite = () =>
    dispatch(
      toggleFavorite({
        virtualFilePath: location.pathname,
        entityType: qsParams.entityType,
        entityKey: headerTitleText,
        queryString: qs.stringify(_.pick(qsParams, ['entityType', 'formKey', 'itmId'])),
      })
    );

  const handleDownloadClick = (type) => {
    if (type !== DOWNLOAD_ICON_ACTIONS_TYPES.DOWNLOAD) {
      return;
    }

    const { itmId, formKey, entityType } = qsParams;
    dispatch(
      downloadFullEntity({
        ItmId: itmId,
        StepId: stepId,
        EntityType: entityType,
        FormKey: formKey,
        WorkFlowId: workflowId,
      })
    );
  };

  return (
    <div className={classes.styledPaper}>
      <div
        className={isMobileDevice ? classes.firstRowMobile : classes.firstRow}
        ui-ta={getUITA('EntityDetailHeaderEntry')}
      >
        <div className={classes.dashboardHeaderStyled}>
          {recordNumber && isOnline && (
            <ToggleButton
              aria-label={translateString(
                isFavorite ? 'Remove from Favorites' : 'Add to Favorites'
              )}
              className={classes.favoriteButton}
              defaultIcon={<StarOutline className={classes.starIcon} />}
              activeIcon={<StarSolid className={classes.starIcon} style={{ fill: primary }} />}
              onChange={handleFavorite}
              checked={isFavorite}
            />
          )}
          {headerTitle}
        </div>
        <div className={classes.pillIconContainer}>
          {!_.isNil(pillText) && !isEmpty(pillText) && (
            <div className={classes.pill}>{pillText}</div>
          )}
          {recordNumber && showDownloadButton && (
            <div className={classes.downloadButton}>
              <DownloadButton actionType={actionType} onClick={handleDownloadClick} />
            </div>
          )}
          {recordNumber && <HelpLink />}
        </div>
      </div>

      <Grid className={classes.detailView} item xs={12}>
        {headerElement.childrenNodes?.map((key) => (
          <DynamicField key={key} id={key} isDetail />
        ))}
      </Grid>
    </div>
  );
};

export default EntityDetailsHeader;
